import type { ReactNode } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { AppLogo } from '@shared/ui';
import { ThemeToggle } from '@widgets/theme-toggle';

const { Title, Paragraph } = Typography;

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

/**
 * Two-column auth layout:
 * - On wide screens (≥960px): brand panel with logo + value props on the left,
 *   form card on the right (mobile-only logo above the form is hidden).
 * - On narrow screens: brand panel collapses; the form column gets a small
 *   logo at the top so the AIDU mark is always visible.
 *
 * Visibility is driven by CSS classes (not inline `display: none`) so the
 * media-query rules below can actually override them.
 */
export function AuthLayout({ title, subtitle, children }: AuthLayoutProps) {
  const { t } = useTranslation();
  const highlights = [
    { title: t('auth.brand.verifiedTitle'), text: t('auth.brand.verifiedText') },
    { title: t('auth.brand.messagingTitle'), text: t('auth.brand.messagingText') },
    { title: t('auth.brand.analyticsTitle'), text: t('auth.brand.analyticsText') },
  ];

  return (
    <div className="auth-shell">
      <aside className="auth-brand brand-gradient">
        <div>
          <AppLogo size={40} />
        </div>
        <div style={{ maxWidth: 460 }}>
          <Title level={2} style={{ margin: 0, fontWeight: 600, fontSize: 36, lineHeight: 1.15 }}>
            {t('auth.brand.title')}
          </Title>
          <Paragraph type="secondary" style={{ marginTop: 16, fontSize: 16, lineHeight: 1.6 }}>
            {t('auth.brand.subtitle')}
          </Paragraph>
        </div>
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
          }}
        >
          {highlights.map((h) => (
            <li key={h.title} style={{ display: 'flex', gap: 12 }}>
              <span
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  background: 'rgba(37, 99, 235, 0.15)',
                  color: '#2563eb',
                  fontWeight: 700,
                }}
              >
                ✓
              </span>
              <div>
                <div style={{ fontWeight: 600 }}>{h.title}</div>
                <div style={{ fontSize: 13, opacity: 0.7 }}>{h.text}</div>
              </div>
            </li>
          ))}
        </ul>
      </aside>

      <main className="auth-form">
        <header className="auth-form__header">
          <div className="auth-form__mobile-logo">
            <AppLogo size={32} />
          </div>
          <ThemeToggle />
        </header>
        <div className="auth-form__center">
          <div className="auth-form__inner">
            <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
              {title}
            </Title>
            {subtitle ? (
              <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 24 }}>
                {subtitle}
              </Paragraph>
            ) : (
              <div style={{ height: 24 }} />
            )}
            {children}
          </div>
        </div>
      </main>

      <style>{`
        .auth-shell {
          display: grid;
          grid-template-columns: 1fr;
          min-height: 100vh;
          background: var(--ant-color-bg-layout);
        }
        .auth-brand {
          display: none;
          padding: 48px;
          flex-direction: column;
          justify-content: space-between;
          gap: 32px;
          color: var(--ant-color-text);
        }
        .auth-form {
          padding: 32px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          background: var(--ant-color-bg-container);
        }
        .auth-form__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .auth-form__mobile-logo { display: flex; }
        .auth-form__center {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .auth-form__inner {
          width: 100%;
          max-width: 420px;
        }
        @media (min-width: 960px) {
          .auth-shell { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
          .auth-brand { display: flex; }
          .auth-form__mobile-logo { display: none; }
        }
      `}</style>
    </div>
  );
}
